import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ImagePlus, X, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CreatePostDialog({ open, onOpenChange, onPostCreated }) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [caption, setCaption] = useState('');
  const [images, setImages] = useState([]); // { file, preview, uploading, path, filename }
  const [publishing, setPublishing] = useState(false);
  const fileInputRef = useRef(null);
  const authHeader = { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` };

  const handleAddImages = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images par publication');
      return;
    }

    for (const file of files) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`${file.name}: Format non supporté`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Fichier trop volumineux (max 10MB)`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      const imgEntry = { file, preview, uploading: true, path: null, filename: file.name };
      setImages(prev => [...prev, imgEntry]);

      // Upload immediately
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/posts/upload-image`, {
          method: 'POST',
          headers: authHeader,
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          setImages(prev => prev.map(img =>
            img.preview === preview ? { ...img, uploading: false, path: data.storage_path, filename: data.original_filename } : img
          ));
        } else {
          toast.error(`Erreur upload: ${file.name}`);
          setImages(prev => prev.filter(img => img.preview !== preview));
        }
      } catch (err) {
        toast.error(`Erreur upload: ${file.name}`);
        setImages(prev => prev.filter(img => img.preview !== preview));
      }
    }
    e.target.value = '';
  };

  const removeImage = (idx) => {
    const img = images[idx];
    if (img.preview) URL.revokeObjectURL(img.preview);
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePublish = async () => {
    if (!caption.trim() && images.length === 0) {
      toast.error('Ajoutez du texte ou des images');
      return;
    }
    if (images.some(img => img.uploading)) {
      toast.error('Attendez la fin des téléchargements');
      return;
    }

    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/api/posts`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caption: caption.trim(),
          image_paths: images.filter(i => i.path).map(i => i.path),
          image_filenames: images.filter(i => i.path).map(i => i.filename)
        })
      });
      if (res.ok) {
        const post = await res.json();
        toast.success(t('blog.postCreated'));
        setCaption('');
        images.forEach(img => { if (img.preview) URL.revokeObjectURL(img.preview); });
        setImages([]);
        onPostCreated?.(post);
        onOpenChange(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur de connexion'); }
    finally { setPublishing(false); }
  };

  const handleClose = () => {
    if (!publishing) {
      images.forEach(img => { if (img.preview) URL.revokeObjectURL(img.preview); });
      setCaption('');
      setImages([]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{t('blog.newPostTitle')}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Image Preview Grid */}
          {images.length > 0 && (
            <div className={`grid gap-2 ${images.length === 1 ? 'grid-cols-1' : images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {images.map((img, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden bg-muted">
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  {img.uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                  <button onClick={() => removeImage(idx)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Images Button */}
          {images.length < 5 && (
            <div>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleAddImages} data-testid="post-image-input" />
              <Button variant="outline" className="w-full h-20 border-dashed" onClick={() => fileInputRef.current?.click()} data-testid="add-images-btn">
                <div className="flex flex-col items-center gap-1">
                  <ImagePlus className="w-6 h-6 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {t('blog.addPhotos')} ({images.length}/5)
                  </span>
                </div>
              </Button>
            </div>
          )}

          {/* Caption */}
          <Textarea
            placeholder={t('blog.describeWork')}
            value={caption}
            onChange={e => setCaption(e.target.value)}
            className="min-h-[100px] resize-none"
            data-testid="post-caption-input"
          />

          {/* Publish */}
          <Button className="btn-primary w-full h-12" onClick={handlePublish} disabled={publishing || (!caption.trim() && images.length === 0)} data-testid="publish-post-btn">
            {publishing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('blog.publishing')}</>
            ) : (
              <><Send className="w-4 h-4 mr-2" />{t('blog.publish')}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
